// Copyright (C) Microsoft Corporation. All rights reserved.

import type { Prompt } from "@1js/pl-card-components";
import {
  AudienceGroup,
  type App,
  type Release,
  type Session,
  type User,
} from "@microsoft/oteljs";
import type { CoreFields } from "@microsoft/oteljs-1ds";

/** Telemetry App Name */
export const APP_NAME = "Copilot Lab";

/** Aria ingestion key */
export const ARIA_INGESTION_KEY =
  "08ef9c3b37c94441a974354b2f79821c-46f6063b-b699-4fab-b864-e332eb39da22-7402";

/** Returns true if the app is running in production, false otherwise */
export const isProduction = () =>
  readFromMetaTags("awa-env")?.toLowerCase() === "production";

/** Returns true if the app is running in staging, false otherwise */
export const isStaging = () =>
  readFromMetaTags("awa-env")?.toLowerCase() === "staging";

/**
 * Defines the users data boundary settings
 */
export enum DataBoundary {
  WW = "WW",
  EU = "EU",
}

/**
 * Defines the action input for a given telemetry event
 */
export enum ActionInputMethods {
  None = "None", // No Input Method.
  Mouse = "Mouse",
  Keyboard = "Keyboard",
  All = "All", // Available via all known electronic input methods.
}

/**
 * Defines the action target for a give telemetry event
 */
export enum ActionTarget {
  None = "None", // No Intended Target.
  ScrollArea = "ScrollArea",
  Button = "Button",
  Toggle = "Toggle",
  TextField = "TextField",
  DropDown = "DropDown",
}

/**
 * Defines the ype of action represent for the feature
 * https://eng.ms/docs/experiences-devices/data-platform-growth/ideas/ideas-wiki/data-platform/ideas-onboarding/feature-onboarding
 */
export enum FeatureActionType {
  IsOffered = "IsOffered", // A user is targeted for a specific feature
  IsTried = "IsTried", // The user has tried the feature
}

/**
 * Session ID used as Part A fields
 * https://eng.ms/docs/experiences-devices/opg/office-fuel/dig/unified-telemetry-documentation/unified-telemetry/sdks/js/sinks/onedssink
 */
let sessionId: string | undefined = undefined;

/**
 * Returns the Core Fields required to initialize a 1DS sink
 * The fields are read from the current page's meta tags in <head>
 * https://eng.ms/docs/experiences-devices/opg/office-fuel/dig/unified-telemetry-documentation/unified-telemetry/eventschema/unifiedeventschemacontracts
 * @returns 1DS Core Fields
 */
export function get1DSCoreFieldsFromMetaTags(): CoreFields {
  let user: User.StrictContract = {
    isSignedIn: false,
  };

  const authType = readFromMetaTags("awa-authtype")?.toLowerCase() || "";
  if (authType) {
    if (authType === "orgid") {
      user = {
        primaryIdentityHash: readFromMetaTags("awa-objectid"),
        tenantId: readFromMetaTags("awa-tenantid"),
        primaryIdentitySpace: "UserObjectId",
        isSignedIn: true,
      } as User.StrictContractKnownUser;
    } else if (authType === "msa") {
      user = {
        primaryIdentityHash: readFromMetaTags("awa-cid")
          ?.replace("m:", "")
          ?.toUpperCase(),
        primaryIdentitySpace: "MSACID",
        isSignedIn: true,
      } as User.StrictContractKnownUser;
    }
  }

  const app: App.StrictContract = {
    platform: "Web",
    name: APP_NAME,
    version: readFromMetaTags("awa-stv") || "",
  };
  const session: Session.Contract = {
    id: getSessionId(),
  };
  const release: Release.Contract = {
    audienceGroup: isProduction()
      ? AudienceGroup.Production
      : AudienceGroup.Microsoft,
  };

  return {
    user,
    app,
    session,
    release,
  };
}

/**
 * Reads the current page' meta tags
 * @param metaTagName Meta tag name
 * @returns Meta tag content
 */
export const readFromMetaTags = (metaTagName: string): string | undefined => {
  return document
    .querySelector(`meta[name="${metaTagName}"]`)
    ?.getAttribute("content")
    ?.trim();
};

/**
 * Gets or creates a random session ID
 * @returns Random session ID
 */
export function getSessionId(): string {
  if (!sessionId) {
    sessionId = window.crypto.randomUUID();
  }
  return sessionId;
}

/**
 * Returns the user's data boundary settings based on the page settings
 * @returns DataBoundary enum
 */
export function getDataBoundary(): DataBoundary {
  if (
    readFromMetaTags("awa-dataBoundary")?.toLowerCase() ===
    DataBoundary.EU.toLowerCase()
  ) {
    return DataBoundary.EU;
  }

  return DataBoundary.WW;
}

/**
 * Returns the telemetry data field
 * @param key Object key to convert to data field
 * @returns data field name
 */
export function getDataFieldName<T extends Prompt, K extends keyof T & string>(
  key: K | "Featured" | "IsFavorite" | "ProductTried" | "Result"
): string {
  return `Prompt.${key}`;
}
